import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api/v1';

// Endpoints di autenticazione che richiedono token JWT
const AUTH_ENDPOINTS = [
  '/auth/login/',
  '/auth/register/',
  '/auth/token/refresh/',
];

const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

let isRefreshing = false;
let pendingRequests = [];

function setTokens({ access, refresh }) {
  if (access) localStorage.setItem('access', access);
  if (refresh) localStorage.setItem('refresh', refresh);
}

function subscribeTokenRefresh(callback) {
  pendingRequests.push(callback);
}

function onRefreshed(newToken) {
  pendingRequests.forEach(callback => callback(newToken));
  pendingRequests = [];
}

function clearTokens() {
  localStorage.removeItem('access');
  localStorage.removeItem('refresh');
  localStorage.removeItem('user');
}

function handleAuthFailure() {
  clearTokens();
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
}

function getAccessToken() {
  return localStorage.getItem('access');
}

function getRefreshToken() {
  return localStorage.getItem('refresh');
}

const PUBLIC_READ_ENDPOINTS = ['/datasets/', '/contests/'];
const SAFE_METHODS = ['get', 'head', 'options'];

function isAuthEndpoint(url) {
  return AUTH_ENDPOINTS.some(e => url?.includes(e));
}

function isPublicReadRequest(url, method) {
  const isSafe = SAFE_METHODS.includes((method || 'get').toLowerCase());
  const isPublic = PUBLIC_READ_ENDPOINTS.some(e => url?.includes(e));
  return isSafe && isPublic;
}

// Request interceptor: aggiunge il token JWT solo per endpoint protetti
apiClient.interceptors.request.use((config) => {
  if (isAuthEndpoint(config.url)) return config;
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Response interceptor: gestisce il refresh del token per endpoint protetti
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      isAuthEndpoint(originalRequest?.url) ||
      isPublicReadRequest(originalRequest?.url, originalRequest?.method) ||
      error.response?.status !== 401 ||
      originalRequest._retry
    ) {
      return Promise.reject(error);
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      handleAuthFailure();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          resolve(apiClient(originalRequest));
        });
      });
    }

    isRefreshing = true;
    originalRequest._retry = true;

    try {
      const { data } = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
        refresh: refreshToken,
      });

      setTokens({ access: data.access });
      onRefreshed(data.access);
      originalRequest.headers.Authorization = `Bearer ${data.access}`;

      return apiClient(originalRequest);
    } catch (err) {
      handleAuthFailure();
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

export default apiClient;
